<template>
  <div data-module="work-header">
    <div class="image-background"
      :style="bgImageStyles"
      ref="headerHeroImage"
    />
    <div class="work-layout">
      <div class="work-main">
        <div class="work-main-content">
          <h1>{{data.title}}</h1>
          <div class="content">
            <markdown class="markdown">{{data.content}}</markdown>
          </div>
        </div>
      </div>
      <div class="work-sidebar">
        <div class="work-sidebar-bg">
          <div class="work-sidebar-content">
            <h2>Our role</h2>
            <ul class="roles">
              <li v-for="role in data.roles" class="role" :key="role">
                {{role}}
              </li>
            </ul>
            <div class="white" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
/*
  Work section Header module

  data record:
  {
    image: <string> // url
    title: <string>
    content: <markdown>
    roles: Array<string> //
  }
*/
// Lib
import Vue from 'vue';

// Utils
import { getResponsiveUrl } from '@/utils/image-utils';

import WorkMixin from './WorkMixin';

export default {

  name: 'work-header',

  props: ['data'],

  mixins: [WorkMixin],

  data() {
    return {
      imageContentWidth: 0,
    };
  },

  computed: {

    bgImageStyles() {
      const { imageContentWidth } = this;
      const { image } = this.data;
      const { hAlign: hA, vAlign: vA, src } = image;

      // add defaults
      const hAlign = hA || 'center';
      const vAlign = vA || 'center';

      const rUrl = getResponsiveUrl(src, imageContentWidth);
      return {
        backgroundImage: `url(${rUrl})`,
        backgroundPosition: `${hAlign} ${vAlign}`,
      };
    },

  },

  methods: {

    measureLayout() {
      this.imageContentWidth = this.getImageContentWidth();
    },

    getImageContentWidth() {
      const el = this.$refs.headerHeroImage;
      return (el) ? el.clientWidth : 0;
    },

  },

  watch: {

    currentWidth(/* newVal, oldVal */) {
      Vue.nextTick(() => {
        this.measureLayout();
      });
    },

  },

  mounted() {
    this.measureLayout();
  },

};
</script>

<style lang="scss">
@import '~@/styles/_variables.scss';
@import '~@/styles/_mixins.scss';
@import '~@/styles/_grid.scss';
@import '~@/styles/_typography.scss';

$works-hero-height-desktop: 536px;
$works-hero-height-tablet: 444px;
$works-header-overlay: 62px;

$layout--percentage: $layout--max-content-width-ng / $layout--optimal-desktop * 100%;


[data-module="work-header"] {
  width: 100%;

  // media query props
  margin-top: rem($height--nav-bar-mobile * 1px); // adjust for nav bar
  margin-bottom: rem($layout--default-module-spacing-mobile);

  @include ngMq($nbp--tablet, up) {
    margin-top: rem($height--nav-bar-tablet * 1px);
    margin-bottom: rem($layout--default-module-spacing-tablet);
  }

  @include ngMq($nbp--desktop, up) {
    margin-top: rem($height--nav-bar-desktop * 1px);
    margin-bottom: rem($layout--default-module-spacing-desktop);
  }

  // media query props
  .image-background {
    width: 100%;

    overflow: hidden;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;

    // media query props
    width: 100vw;
    height: 100vw;

    @include ngMq($nbp--tablet, up) {
      width: 100vw;
      height: rem($works-hero-height-tablet);
    }

    @include ngMq($nbp--desktop, up) {
      width: 100%;
      height: rem($works-hero-height-desktop);
    }
  }

  .work-layout {

    // media query props
    margin-top: 0;
    @include ngGrid('mobile');

    @include ngMq($nbp--tablet, up) {
      margin-top: 0;
      @include ngGrid('tablet');
    }

    @include ngMq($nbp--desktop, up) {
      margin-top: rem(-$works-header-overlay);
      @include ngGrid('desktop');
    }

    .work-main {
      background-color: white;

      // media query props
      grid-column: 1 / span 6;
      margin-right: 0;

      @include ngMq($nbp--tablet, up) {
        grid-column: 1 / span 8;
        margin-right: 0;
      }

      @include ngMq($nbp--desktop, up) {
        grid-column: 1 / span 8;
        margin-right: rem($grid-gap-desktop * -1px); // pull to right, column
      }

      .work-main-content {
        // media query props
        margin: 0;

        @include ngMq($nbp--tablet, up) {
          margin: 0;
        }

        @include ngMq($nbp--desktop, up) {
          margin: 0 rem(($grid-column-desktop) * 1px);
        }

        h1 {

          // media query props
          @include serif( $size: (35, 45, 0) );
          margin-top: rem(46px); // title push

          @include ngMq($nbp--tablet, up) {
            @include serif( $size: (40, 63, 0) );
            margin-top: rem(52px);
          }

          @include ngMq($nbp--desktop, up) {
            @include serif( $size: (48, 63, 0) );
            margin-top: rem(54px);
          }

          &::after {
            content: '';
            display: block;
            margin-top: rem(24px);
            padding-bottom: 5px; // height of element
            background: url('~@/assets/ornaments/linear-dots.svg') no-repeat left center;
          }
        }

        .content {
          margin-top: rem(24px); // betwen element and body
        }

      }

    }

    .work-sidebar {

      // media query props
      display: none;
      // grid-column: 1 / span 6;
      // margin-top:
      // margin-right:

      @include ngMq($nbp--tablet, up) {
        display: block;
        grid-column: 9 / span 4;
        margin-top: 0;
        margin-right: rem($grid-padding-tablet * -1px); // pull to right edge
      }

      @include ngMq($nbp--desktop, up) {
        display: block;
        grid-column: 9 / span 4;
        margin-top: $works-header-overlay; // push down from image
        margin-right: 0;
      }

      .work-sidebar-bg {
        background-color: #f0f0f0;

        // media query props
        // padding-bottom: rem(76px);

        @include ngMq($nbp--tablet, up) {
          padding-bottom: rem(76px);
        }

        @include ngMq($nbp--desktop, up) {
          padding-bottom: rem(68px);
        }
      }

      .work-sidebar-content {

        // not displayed for mobile !!

        // media query props
        // padding-top: 0;
        // margin-left: 0;
        // margin-top: 0;

        @include ngMq($nbp--tablet, up) {
          margin-top: 0;
          padding-top: rem(78px); // title push
          margin-left: rem($grid-column-tablet * 1px);
        }

        @include ngMq($nbp--desktop, up) {
          padding-top: rem(68px); // title push
          margin-left: rem($grid-column-desktop * 1px);
        }

        h2 {
          @include sansserif( $size: (18, 18, 0), $weight: 'light' );
          // font-size: rem(18px);
          line-height: 1;
          color: $color--primary-midtone;
          margin-bottom: rem(14px);
        }

        .roles {
          margin-top:rem(20px); // push roles down
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .role {
          @include sansserif( $size: (14, 33, 0) );
          color: #333;
        }

        .white {
          width: 100%;
          height: 100%;
        }

      }
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/modules/work/Header.vue